import { useChat, useRoomContext } from '@livekit/components-react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { format } from '../i18n';
import { useStrings } from '../i18n/context';
import { FILES_TOPIC } from '../protocol';

/** The largest file sent through the room's data channel (it goes to every participant). */
export const MAX_FILE_MB = 25;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const MAX_MESSAGE_CHARS = 2000;
const MAX_SHOWN_CHARS = 4000;
const MAX_ENTRIES = 500;

interface ReceivedFile {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly from: string;
  readonly at: number;
  /** An object URL of the bytes, revoked when the panel goes. */
  readonly url: string;
}

type Entry =
  | { readonly kind: 'text'; readonly key: string; readonly at: number; readonly from: string; readonly text: string }
  | { readonly kind: 'file'; readonly key: string; readonly at: number; readonly from: string; readonly file: ReceivedFile };

interface Props {
  readonly open: boolean;
  readonly names: ReadonlyMap<string, string>;
  readonly localIdentity: string;
  readonly onClose: () => void;
  /** How many entries the panel holds: the room counts the unread ones while it is closed. */
  readonly onTotal: (total: number) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeOf(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Written messages (LiveKit's chat, topic lk.chat) and files (byte streams on vm.files)
 * between the people in the room: they travel through the room's data channel to the
 * participants and are stored nowhere. Stays mounted while closed, because a byte stream with
 * no handler on its topic is dropped. Text is rendered as text; a file is a download link.
 */
export function ChatPanel({ open, names, localIdentity, onClose, onTotal }: Props) {
  const strings = useStrings();
  const room = useRoomContext();
  const { chatMessages, send, isSending } = useChat();
  const [files, setFiles] = useState<readonly ReceivedFile[]>([]);
  const [draft, setDraft] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const urls = useRef<readonly string[]>([]);

  // Files from the others: one handler per topic for the life of the room.
  useEffect(() => {
    room.registerByteStreamHandler(FILES_TOPIC, (reader, participant) => {
      const info = reader.info;
      if ((info.size ?? 0) > MAX_FILE_BYTES) return;
      void reader.readAll().then((chunks) => {
        const blob = new Blob(
          chunks.map((chunk) => new Uint8Array(chunk)),
          { type: info.mimeType },
        );
        if (blob.size > MAX_FILE_BYTES) return;
        const file: ReceivedFile = {
          id: info.id,
          name: info.name,
          size: blob.size,
          from: participant.identity,
          at: info.timestamp,
          url: URL.createObjectURL(blob),
        };
        setFiles((current) => [...current, file].slice(-MAX_ENTRIES));
      });
    });
    return () => room.unregisterByteStreamHandler(FILES_TOPIC);
  }, [room]);

  // The object URLs live as long as the panel.
  useEffect(() => {
    urls.current = files.map((file) => file.url);
  }, [files]);
  useEffect(
    () => () => {
      for (const url of urls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  const entries = useMemo<Entry[]>(() => {
    const texts: Entry[] = chatMessages.map((message) => ({
      kind: 'text',
      key: message.id,
      at: message.timestamp,
      from: message.from?.identity ?? '',
      text: message.message.slice(0, MAX_SHOWN_CHARS),
    }));
    const sent: Entry[] = files.map((file) => ({ kind: 'file', key: `file-${file.id}`, at: file.at, from: file.from, file }));
    return [...texts, ...sent].toSorted((a, b) => a.at - b.at).slice(-MAX_ENTRIES);
  }, [chatMessages, files]);

  useEffect(() => onTotal(entries.length), [entries.length, onTotal]);

  // Follow the newest entry while the reader is at the bottom.
  useEffect(() => {
    const element = list.current;
    if (!element || !open || entries.length === 0) return;
    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
    if (nearBottom) element.scrollTop = element.scrollHeight;
  }, [entries, open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || isSending) return;
    setDraft('');
    setStatus('');
    try {
      await send(text);
    } catch {
      setStatus(strings.chatSendFailed);
    }
  }

  async function sendFile(file: File) {
    setStatus('');
    if (file.size > MAX_FILE_BYTES) {
      setStatus(format(strings.chatFileTooLarge, { max: MAX_FILE_MB }));
      return;
    }
    setProgress(0);
    try {
      const { id } = await room.localParticipant.sendFile(file, {
        topic: FILES_TOPIC,
        mimeType: file.type || 'application/octet-stream',
        onProgress: (value) => setProgress(Math.round(value * 100)),
      });
      const mine: ReceivedFile = {
        id,
        name: file.name,
        size: file.size,
        from: localIdentity,
        at: Date.now(),
        url: URL.createObjectURL(file),
      };
      setFiles((current) => [...current, mine].slice(-MAX_ENTRIES));
    } catch {
      setStatus(strings.chatFileFailed);
    } finally {
      setProgress(null);
    }
  }

  function nameOf(identity: string): string {
    const name = names.get(identity) || strings.someone;
    return identity === localIdentity ? format(strings.you, { name }) : name;
  }

  return (
    <aside id="vm-chat" className="notes chat" aria-labelledby="vm-chat-heading" hidden={!open}>
      <div className="notes__header">
        <h2 id="vm-chat-heading">{strings.chatHeading}</h2>
        <button type="button" className="button" onClick={onClose}>
          {strings.chatClose}
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="notes__empty">{strings.chatEmpty}</p>
      ) : (
        <ol ref={list} className="panel__list" aria-label={strings.chatMessagesLabel}>
          {entries.map((entry) => (
            <li key={entry.key} className={entry.from === localIdentity ? 'panel__entry panel__entry--mine' : 'panel__entry'}>
              <span className="panel__meta">
                <time>{timeOf(entry.at)}</time> {nameOf(entry.from)}
              </span>
              {entry.kind === 'text' ? (
                <span className="chat__text">{entry.text}</span>
              ) : (
                <a className="chat__file" href={entry.file.url} download={entry.file.name}>
                  {format(strings.chatFile, { name: entry.file.name, size: formatSize(entry.file.size) })}
                  <span className="chat__download">{strings.chatDownload}</span>
                </a>
              )}
            </li>
          ))}
        </ol>
      )}
      <form className="chat__form" onSubmit={(event) => void submit(event)}>
        <label htmlFor="vm-chat-input" className="visually-hidden">
          {strings.chatMessageLabel}
        </label>
        <input
          id="vm-chat-input"
          className="chat__input"
          value={draft}
          maxLength={MAX_MESSAGE_CHARS}
          placeholder={strings.chatPlaceholder}
          autoComplete="off"
          onChange={(event) => setDraft(event.target.value)}
        />
        <button type="submit" className="button" disabled={!draft.trim() || isSending}>
          {strings.chatSend}
        </button>
        {/* Hidden: the button after it opens the chooser (a hidden file input still takes click()). */}
        <input
          ref={fileInput}
          type="file"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void sendFile(file);
          }}
        />
        <button type="button" className="button" disabled={progress !== null} onClick={() => fileInput.current?.click()}>
          {progress === null ? strings.chatSendFile : format(strings.chatSending, { percent: progress })}
        </button>
      </form>
      <output className="chat__status">{status}</output>
    </aside>
  );
}
